import { get } from 'lodash-es';
import { useObservableState } from 'observable-hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { useOverrideState, useSharedFormContext } from '../../utils';
import { PROJECT_OPTION } from '../ChoiceField';
import type { DaySchedule, PROJECT_OPTION_TYPE, WeekDay, XAble } from './use-schedule-manager';
import { getPresetForDaySchedule, Measure, presets, translations } from './use-schedule-manager';

interface UseScheduleRowProps {
    /**
     * The name of the field in the form context, e.g. 'schedule.monday'.
     */
    schedule: DaySchedule;
    /**
     * The day of the week for which the schedule is being managed, e.g. 'monday'.
     */
    weekDay: WeekDay;
    /**
     * The name of the field in the form context.
     * This is used to identify the schedule row in the form.
     */
    name: string;
    /**
     * Callback function to handle changes to the schedule.
     * This function is called with the updated schedule when changes are made.
     */
    onScheduleChange: (schedule: Partial<DaySchedule>) => void;
}

/**
 * Custom hook to manage a single row of the schedule field.
 * It handles the logic for determining whether the schedule is overridden,
 * overridable, and provides functions to handle changes to the preset and time inputs.
 *
 * It exposes functions to handle preset changes, time changes,
 * and provides the current preset and its options.
 */
export function useScheduleRow({ name, schedule, weekDay, onScheduleChange }: UseScheduleRowProps) {
    const { overridden, overridable } = useOverrideState(name);

    const { formController } = useSharedFormContext();
    const parentValues = useObservableState(formController.parentValues$, null);

    const parentValue: DaySchedule = useMemo(
        () => get(parentValues, name, {}) as DaySchedule,
        [parentValues, name]
    );

    // Use the parent value only if the current schedule is overridable and not overridden
    // or if it is not overridable at all.
    useEffect(() => {
        if ((overridable && !overridden) || !overridable) {
            // Keep the child form in sync with the project defaults while the field
            // isn’t explicitly overridden. This mirrors the legacy behaviour where
            // inherited schedules stayed up to date with parent changes.
            onScheduleChange(parentValue);
        }
    }, [parentValue, overridden, overridable, onScheduleChange, weekDay]);

    // Get the preset for the current schedule.
    const preset = useMemo(() => getPresetForDaySchedule(schedule), [schedule]);

    // Determine the preset value based on whether the schedule is overridable and overridden.
    const presetValue = useMemo<Measure | typeof PROJECT_OPTION>(
        () => getPresetForDaySchedule(schedule),
        [schedule]
    );
    // Create the preset options based on translations and whether the field is overridable.
    // If the field is overridable and has a parent value, include the project option.
    const presetOptions = useMemo<Array<[Measure | PROJECT_OPTION_TYPE, string]>>(
        () => Object.entries(translations) as Array<[Measure | PROJECT_OPTION_TYPE, string]>,
        []
    );

    // Handle preset changes. Selecting the project defaults while this row is overriding
    // should restore the parent schedule; every other case reuses the preset lookup, which
    // still falls back to CUSTOM so the row keeps an editable configuration.
    const handlePresetChange = useCallback(
        (value: Measure | PROJECT_OPTION_TYPE) => {
            if (value === PROJECT_OPTION && overridable && overridden) {
                onScheduleChange(parentValue || {});
                return;
            }

            onScheduleChange(get(presets, value, presets[Measure.CUSTOM]));
        },
        [overridable, onScheduleChange, overridden, parentValue]
    );

    // Time changes are handled by updating the schedule with the new enable or disable time.
    const handleTimeChange = useCallback(
        (part: XAble, value: string) => {
            onScheduleChange({ [part]: value });
        },
        [onScheduleChange]
    );

    return {
        handlePresetChange,
        presetOptions,
        preset,
        presetValue,
        handleTimeChange,
        overridden,
        overridable,
    };
}
